import {FC} from 'react';
import {type IBrand} from '@verdocs/js-sdk';
import {GlobeAltIcon} from '@heroicons/react/24/outline';
import {useOrganization} from '../../react-sdk/queries';
import {useRole} from '../../hooks/useRole';
import {AppState} from '../../AppState';

export type TSenderState = 'default' | 'pending' | 'active';

const DEFAULT_FROM_ADDRESS = 'notifications@verdocs.com';

/**
 * The From line as the api assembles it (generateEmailFrom): the brand's From name, then the legacy display name
 * from the custom sender form, then the sender's own name on personal accounts or the organization name for
 * everyone else. The address is the custom sender only once it verifies.
 */
export const useFromLine = (brand: IBrand) => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const orgQuery = useOrganization(organizationId);
  const {isPersonal} = useRole();
  const profile = AppState.currentProfile;

  const fallbackName =
    brand.email_display_name ||
    (isPersonal ? `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() : orgQuery.data?.name || brand.organization?.name) ||
    'Your organization';
  const state: TSenderState = !brand.email_domain
    ? 'default'
    : brand.email_domain_status === 'verified' && brand.email_local_part
      ? 'active'
      : 'pending';

  return {
    fallbackName,
    name: brand.email_sender_name?.trim() || fallbackName,
    address: state === 'active' ? `${brand.email_local_part}@${brand.email_domain}`.toLowerCase() : DEFAULT_FROM_ADDRESS,
    state,
    isPersonal,
  };
};

const WindowDots: FC = () => (
  <div className="flex shrink-0 gap-1.5">
    <div className="h-3 w-3 rounded-full bg-red-400" />
    <div className="h-3 w-3 rounded-full bg-yellow-400" />
    <div className="h-3 w-3 rounded-full bg-green-400" />
  </div>
);

/** A mail client's message header for the full-size invitation previews. */
export const EmailWindowHeader: FC<{brand: IBrand; subject?: string}> = ({brand, subject = 'Action Required for Form 8878'}) => {
  const from = useFromLine(brand);

  return (
    <div className="border-b border-gray-300 bg-gray-100">
      <div className="flex h-9 items-center px-4">
        <WindowDots />
      </div>
      <div className="grid grid-cols-[64px_minmax(0,1fr)] gap-y-1 bg-white px-4 py-2.5 text-xs">
        <span className="text-gray-400">From</span>
        <span className="truncate">
          <b className="font-semibold text-gray-900">{from.name}</b>{' '}
          <span className={from.state === 'pending' ? 'text-amber-600' : 'text-gray-600'}>&lt;{from.address}&gt;</span>
          {from.state === 'pending' && <span className="ml-1.5 text-[11px] text-amber-600">(custom sender pending)</span>}
        </span>
        <span className="text-gray-400">To</span>
        <span className="truncate text-gray-600">Will Power &lt;will.power@google.com&gt;</span>
        <span className="text-gray-400">Subject</span>
        <span className="truncate font-medium text-gray-800">{subject}</span>
      </div>
    </div>
  );
};

/** A browser window's tab strip, showing the favicon and page title a hosted page carries. */
export const BrowserTabBar: FC<{title: string; faviconUrl?: string | null; compact?: boolean}> = ({title, faviconUrl, compact = false}) => (
  <div className={`flex items-end gap-3 border-b border-gray-200 bg-gray-100 ${compact ? 'px-2.5 pt-2' : 'px-4 pt-2.5'}`}>
    <div className={compact ? 'pb-2.5' : 'pb-3'}>
      <WindowDots />
    </div>
    <div
      className={`flex min-w-0 items-center gap-1.5 rounded-t-lg bg-white ${compact ? 'max-w-[70%] px-2 py-1.5 text-[11px]' : 'max-w-[260px] px-3 py-2 text-xs'}`}>
      {faviconUrl ? (
        // Keyed so a half-typed URL that fails to load does not hide the icon once the URL is fixed.
        <img
          key={faviconUrl}
          src={faviconUrl}
          alt=""
          onError={(e) => (e.currentTarget.style.display = 'none')}
          className="size-3.5 shrink-0 object-contain"
        />
      ) : (
        <GlobeAltIcon className="size-3.5 shrink-0 text-gray-400" />
      )}
      <span className="truncate text-gray-700">{title}</span>
    </div>
  </div>
);
